/**
 * BULUTTAN GETİR — kurulum ekranında, ÖNCE KASA KURMADAN (kullanıcı kararı
 * 19.10.2026). Buluta bağlanılır, yedek seçilir, o kasanın parolası ya da 24
 * kelimesi girilir: profil yedeğin kendi kilidiyle kurulur (yeni parola, yeni
 * 24 kelime yok). Ardından bulut bağlantısı yeni profile yazılır; uygulama
 * açılınca eşitleme kendiliğinden sürer.
 *
 * Bulut yerine BU CİHAZDAKİ bir dosya da seçilebilir (gelen karar 03.11.2026:
 * değişiklikte otomatik indirilen dosya telefonda da geri gelsin). O zaman
 * bulut bağlantısı yazılmaz.
 *
 * Aynı kasa bu cihazda zaten bir profilse Worker reddeder ("… profili. Onu
 * açın.") ve hiçbir şey yazılmaz. Ad boş bırakılırsa yedeğin şifreli içindeki
 * kasa adı kullanılır.
 */

import { M, YEREL } from "../../dil";
import { useMemo, useRef, useState } from "react";
import { readFileAsText } from "../../lib/files";
import type { LocalRepository } from "@parafon/contracts/local-api";
import { denemeSinirli } from "../../lib/denemeSiniri";
import { useMutation } from "../../repo/useAsync";
import { hedefAc, hedefeBaglan, hedefSecenekleri } from "../../yedek/hedefler";
import { createDefterKasasi, createIndexedDbTutamacDeposu } from "../../yedek/kasa";
import type { TutamacDeposu } from "../../yedek/kasa";
import { bulutIstemcileri } from "../../yedek/oauth";
import type { BulutBagimliliklari, BulutIstemcileri } from "../../yedek/oauth";
import type { BulutDosyasi } from "../../yedek/tipler";
import { VARSAYILAN_BULUT } from "../../yedek/YedekSaglayici";
import type { GeciciHedef } from "../../yedek/YedekSaglayici";
import { SaglayiciSecici } from "../BulutYedegiKarti";
import type { BaglantiKaynagi } from "../BulutYedegiKarti";
import { Button, Field, InlineError, TextArea, TextInput } from "../../ui/primitives";

function zaman(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString(YEREL, { dateStyle: "short", timeStyle: "short" });
}

/** En yeni önce; eşitlikte "-son" dosyası önce (her cihazın en güncel kopyası). */
function sirala(dosyalar: readonly BulutDosyasi[]): BulutDosyasi[] {
  return [...dosyalar].sort((a, b) => {
    const z = (b.degisti ?? "").localeCompare(a.degisti ?? "");
    if (z !== 0) return z;
    return Number(b.ad.endsWith("-son.pfai")) - Number(a.ad.endsWith("-son.pfai"));
  });
}

export function BuluttanGetir({
  repo,
  onBitti,
  istemci,
  bulut,
  tutamaclar,
}: {
  readonly repo: LocalRepository;
  readonly onBitti: () => void;
  /** Testler sahte bulut geçirir; üretimde ortamın kayıtları ve gerçek fetch. */
  readonly istemci?: BulutIstemcileri;
  readonly bulut?: BulutBagimliliklari;
  readonly tutamaclar?: TutamacDeposu;
}) {
  const kilit = repo.kilit;
  const istemciler = useMemo(() => istemci ?? bulutIstemcileri(), [istemci]);
  const d = bulut ?? VARSAYILAN_BULUT;
  const kaynak = useMemo<BaglantiKaynagi>(
    () => ({
      secenekler: hedefSecenekleri(istemciler),
      async baglan(tur, sunucu) {
        const kayit = await hedefeBaglan(tur, istemciler, d, sunucu);
        return { kayit, hedef: hedefAc(kayit, istemciler, d) };
      },
      // Henüz kasa yok: parola şartı yalnızca var olan kasanın kopyasında aranmaz.
      parolaDogrula: () => null,
    }),
    [istemciler, d],
  );
  const baglanRun = useMutation();
  const getirRun = useMutation();
  const [baglanti, setBaglanti] = useState<{ h: GeciciHedef; dosyalar: readonly BulutDosyasi[] } | null>(null);
  const [dosya, setDosya] = useState<{ readonly ad: string; readonly metin: string } | null>(null);
  const dosyaRef = useRef<HTMLInputElement | null>(null);
  const [secili, setSecili] = useState<string | null>(null);
  const [kip, setKip] = useState<"parola" | "ifade">("parola");
  const [parola, setParola] = useState("");
  const [ifade, setIfade] = useState("");
  const [etiket, setEtiket] = useState("");
  if (kilit?.yedektenKur === undefined) return null;
  const yedektenKur = kilit.yedektenKur.bind(kilit);

  const onBaglanti = (b: Promise<GeciciHedef>): void => {
    void baglanRun.run(async () => {
      const h = await b;
      const dosyalar = sirala((await h.hedef.listele()).filter((x) => x.ad.endsWith(".pfai")));
      if (dosyalar.length === 0) throw new Error(M.kilit.getir.yedekYok);
      setBaglanti({ h, dosyalar });
      setSecili(dosyalar[0]?.ad ?? null);
    });
  };

  const onDosya = (f: File): void => {
    void baglanRun.run(async () => {
      setDosya({ ad: f.name, metin: await readFileAsText(f) });
    });
    if (dosyaRef.current !== null) dosyaRef.current.value = "";
  };

  const onGetir = (): void => {
    const secim = dosya !== null ? dosya : baglanti !== null && secili !== null ? { h: baglanti.h, ad: secili } : null;
    if (secim === null) return;
    void getirRun.run(async () => {
      const metin = "metin" in secim ? secim.metin : await secim.h.hedef.oku(secim.ad);
      const kelimeler = ifade.split(/\s+/).filter((w) => w.length > 0);
      const sir = kip === "ifade" ? { kelimeler } : { parola };
      const ad = etiket.trim();
      await denemeSinirli(() => yedektenKur(metin, sir, ad.length > 0 ? ad : undefined));
      await repo.open();
      if (dosya === null && baglanti !== null) {
        // Bu cihaz aynı yere bağlı kalsın: eşitleme açılışta kendiliğinden.
        const kasa = createDefterKasasi(kilit, { tutamaclar: tutamaclar ?? createIndexedDbTutamacDeposu() });
        await kasa.yaz({ surum: 2, hedef: baglanti.h.hedef.kayit(), son: null, hata: null, eskiTemizlendi: true, adGocu: true });
      }
      setParola("");
      setIfade("");
      onBitti();
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed text-ink">{M.kilit.getir.aciklama}</p>
      {baglanti === null && dosya === null ? (
        <>
          <SaglayiciSecici
            kaynak={kaynak}
            ad="getir-saglayici"
            kodSecimi={false}
            parolaSor={false}
            mesgul={baglanRun.pending}
            dugme={M.kilit.getir.baglanVeListele}
            baslik={M.kilit.getir.kasanizNerede}
            onBaglanti={onBaglanti}
            onHata={(e) => void baglanRun.run(() => Promise.reject(e))}
          />
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3 text-[12px] text-muted">
            <span>{M.kilit.getir.dosyadan}</span>
            <Button disabled={baglanRun.pending} onClick={() => dosyaRef.current?.click()}>
              {M.kilit.getir.dosyaSec}
            </Button>
            <input
              ref={dosyaRef}
              type="file"
              accept=".pfai"
              className="hidden"
              data-testid="getir-dosya-girdi"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f !== undefined) onDosya(f);
              }}
            />
          </div>
          {baglanRun.error !== undefined && <InlineError error={baglanRun.error} />}
        </>
      ) : (
        <div className="space-y-3">
          {dosya !== null ? (
            <p className="text-[12px] text-ink">{M.kilit.getir.secilenDosya(dosya.ad)}</p>
          ) : baglanti !== null && (
          <fieldset className="grid max-h-56 gap-1 overflow-auto">
            <legend className="mb-1 text-[12px] text-muted">{M.kilit.getir.yedekDosyasi}</legend>
            {baglanti.dosyalar.map((f) => (
              <label key={f.ad} className="flex items-baseline gap-2 text-[12px]">
                <input type="radio" name="getir-dosya" checked={secili === f.ad} onChange={() => setSecili(f.ad)} />
                <span className="pf-fig text-ink">{f.ad}</span>
                <span className="text-faint">{zaman(f.degisti)}</span>
              </label>
            ))}
          </fieldset>
          )}
          <div className="flex gap-3 text-[12px]" role="radiogroup" aria-label={M.kilit.getir.neyleAcacaksiniz}>
            <label className="flex items-center gap-1">
              <input type="radio" name="getir-kip" checked={kip === "parola"} onChange={() => setKip("parola")} />
              {M.kilit.getir.parolayla}
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" name="getir-kip" checked={kip === "ifade"} onChange={() => setKip("ifade")} />
              {M.kilit.getir.ifadeyle}
            </label>
          </div>
          {kip === "ifade" ? (
            <Field label={M.kilit.kurtarmaIfadesi} htmlFor="getir-ifade" hint={M.kilit.getir.ifadeIpucu}>
              <TextArea
                id="getir-ifade"
                rows={3}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={ifade}
                onChange={(e) => setIfade(e.currentTarget.value)}
              />
            </Field>
          ) : (
            <Field label={M.kilit.getir.kasaninParolasi} htmlFor="getir-parola">
              <TextInput
                id="getir-parola"
                type="password"
                autoComplete="current-password"
                value={parola}
                onChange={(e) => setParola(e.currentTarget.value)}
              />
            </Field>
          )}
          <Field label={M.kilit.profilAdi} htmlFor="getir-etiket" hint={M.kilit.getir.profilAdiIpucu}>
            <TextInput id="getir-etiket" maxLength={40} value={etiket} onChange={(e) => setEtiket(e.currentTarget.value)} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={getirRun.pending || (dosya === null && secili === null) || (kip === "ifade" ? ifade.trim().length === 0 : parola.length === 0)}
              onClick={onGetir}
            >
              {getirRun.pending ? M.kilit.getir.getiriliyor : M.kilit.getir.kasayiGetir}
            </Button>
            <Button
              variant="ghost"
              disabled={getirRun.pending}
              onClick={() => {
                setBaglanti(null);
                setDosya(null);
              }}
            >
              {M.kilit.getir.baskaYereBaglan}
            </Button>
          </div>
          {getirRun.error !== undefined && <InlineError error={getirRun.error} />}
        </div>
      )}
    </div>
  );
}
